"use client";
import { useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useQuery, useInfiniteQuery, useQueryClient, QueryClient, keepPreviousData, } from '@tanstack/react-query';
export { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
/**
 * Create a QueryClient with sensible defaults for the AIQ API.
 */
export function createQueryClient(options = {}) {
    return new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 0,
                refetchOnWindowFocus: true,
                refetchOnReconnect: true,
                retry: false,
                ...(options.gcTime !== undefined ? { gcTime: options.gcTime } : {}),
            },
        },
    });
}
export function initAPI(fetcher, axiosClient) {
    async function makeRequest(method, url, data, config = { requiresTokenAuthentication: true }) {
        if (config.requiresClientVerification) {
            config.headers = { ...(config.headers || {}), 'x-aiq-client-verification': true };
        }
        else if (config.requiresTokenAuthentication) {
            config.headers = { ...(config.headers || {}), 'x-aiq-token-auth': true };
        }
        const requestConfig = {
            ...(config || {}),
            url,
            method,
            data
        };
        if (config && config.useAbsoluteUrl) {
            return axios(requestConfig).then(res => res.data);
        }
        try {
            const res = await axiosClient(requestConfig);
            return res.data;
        }
        catch (e) {
            throw e;
        }
    }
    async function post(url, data, config = { requiresTokenAuthentication: true }) {
        return makeRequest('post', url, data, config);
    }
    ;
    async function put(url, data, config = { requiresTokenAuthentication: true }) {
        return makeRequest('put', url, data, config);
    }
    async function patch(url, data, config = { requiresTokenAuthentication: true }) {
        return makeRequest('patch', url, data, config);
    }
    async function del(url, config = { requiresTokenAuthentication: true }) {
        return makeRequest('delete', url, undefined, config);
    }
    async function get(url, config = { requiresTokenAuthentication: true }) {
        return makeRequest('get', url, undefined, config);
    }
    // Front facing method to access data from the Angling iQ api
    function useApi(key, opts) {
        const headers = {};
        if (opts && opts.disableTokenAuthentication) {
            headers['x-aiq-token-auth'] = false;
        }
        else {
            headers['x-aiq-token-auth'] = true;
        }
        const queryClient = useQueryClient();
        const fetchFn = fetcher(headers);
        // Build query options inline to preserve generic types
        const queryOptions = {
            queryKey: [key],
            queryFn: () => fetchFn(key),
            enabled: key !== null,
        };
        if (opts) {
            if (opts.revalidateIfStale === false) {
                queryOptions.staleTime = Infinity;
            }
            if (opts.revalidateOnFocus !== undefined) {
                queryOptions.refetchOnWindowFocus = opts.revalidateOnFocus;
            }
            if (opts.revalidateOnReconnect !== undefined) {
                queryOptions.refetchOnReconnect = opts.revalidateOnReconnect;
            }
            if (opts.revalidateOnMount !== undefined) {
                queryOptions.refetchOnMount = opts.revalidateOnMount;
            }
            if (opts.fallbackData !== undefined) {
                queryOptions.placeholderData = { meta: { code: 200, status_code: 200, message: 'Success' }, data: opts.fallbackData };
            }
            if (opts.keepPreviousData) {
                queryOptions.placeholderData = keepPreviousData;
            }
            if (opts.refreshInterval) {
                queryOptions.refetchInterval = opts.refreshInterval;
            }
        }
        const { data, error, isFetching, isLoading, refetch } = useQuery(queryOptions);
        const mutate = useCallback(async (newData, shouldRevalidate) => {
            if (newData !== undefined) {
                // Resolve the new data
                let resolved;
                if (typeof newData === 'function') {
                    const currentCacheData = queryClient.getQueryData([key]);
                    resolved = newData(currentCacheData?.data);
                }
                else {
                    resolved = await newData;
                }
                if (resolved !== undefined) {
                    const currentCacheData = queryClient.getQueryData([key]);
                    const meta = currentCacheData?.meta || { code: 200, status_code: 200, message: 'Success' };
                    queryClient.setQueryData([key], {
                        meta,
                        data: resolved,
                        pagination: currentCacheData?.pagination,
                    });
                }
                if (shouldRevalidate !== false) {
                    await refetch();
                }
                return resolved;
            }
            // No data provided -- just revalidate
            const result = await refetch();
            return result.data?.data;
        }, [key, queryClient, refetch]);
        return {
            data: data?.data,
            meta: data?.meta,
            pagination: data?.pagination,
            isValidating: isFetching,
            isLoading,
            error: error ?? undefined,
            mutate,
        };
    }
    function useApiPagination(getKeyFn, opts) {
        const headers = {};
        if (opts && opts.disableTokenAuthentication) {
            headers['x-aiq-token-auth'] = false;
        }
        else {
            headers['x-aiq-token-auth'] = true;
        }
        const queryClient = useQueryClient();
        const fetchFn = fetcher(headers);
        // Derive a stable base key from the first page key
        const firstPageKey = getKeyFn(0, undefined);
        // Store the getKeyFn in a ref so the queryFn always uses the latest version
        const getKeyFnRef = useRef(getKeyFn);
        getKeyFnRef.current = getKeyFn;
        const queryOptions = {};
        if (opts?.revalidateIfStale === false) {
            queryOptions.staleTime = Infinity;
        }
        if (opts?.revalidateOnFocus !== undefined) {
            queryOptions.refetchOnWindowFocus = opts.revalidateOnFocus;
        }
        if (opts?.revalidateOnReconnect !== undefined) {
            queryOptions.refetchOnReconnect = opts.revalidateOnReconnect;
        }
        if (opts?.keepPreviousData) {
            queryOptions.placeholderData = keepPreviousData;
        }
        if (opts?.fallbackData) {
            queryOptions.placeholderData = {
                pages: [{ data: opts.fallbackData }],
                pageParams: [0],
            };
        }
        const { data: infiniteData, error, isFetching, isLoading, fetchNextPage, hasNextPage, refetch, } = useInfiniteQuery({
            queryKey: ['infinite', firstPageKey],
            queryFn: async ({ pageParam }) => {
                const pageIndex = pageParam;
                // Get the previous page data to pass to getKeyFn
                const currentData = queryClient.getQueryData(['infinite', firstPageKey]);
                const previousPageData = pageIndex > 0 && currentData?.pages
                    ? currentData.pages[pageIndex - 1]
                    : undefined;
                const url = getKeyFnRef.current(pageIndex, previousPageData);
                if (!url) {
                    throw new Error('No more pages');
                }
                return fetchFn(url);
            },
            initialPageParam: 0,
            getNextPageParam: (lastPage, allPages) => {
                // Check if there are more pages by trying the next key
                const nextIndex = allPages.length;
                const nextKey = getKeyFnRef.current(nextIndex, lastPage);
                return nextKey !== null ? nextIndex : undefined;
            },
            enabled: firstPageKey !== null,
            ...queryOptions,
        });
        const pages = infiniteData?.pages;
        const size = pages?.length ?? 0;
        const isLoadingMore = isFetching && size > 0 && !isLoading;
        const flattenedData = useMemo(() => pages?.flatMap(x => x.data), [pages]);
        const pagination = pages && pages.length > 0 ? pages[pages.length - 1].pagination : undefined;
        const setSize = useCallback((sizeOrUpdater) => {
            const currentSize = infiniteData?.pages?.length ?? 0;
            const newSize = typeof sizeOrUpdater === 'function' ? sizeOrUpdater(currentSize) : sizeOrUpdater;
            if (newSize > currentSize) {
                // Fetch more pages
                for (let i = currentSize; i < newSize; i++) {
                    fetchNextPage();
                }
            }
        }, [infiniteData, fetchNextPage]);
        const mutate = useCallback(async (newData, shouldRevalidate) => {
            if (newData !== undefined) {
                let resolved;
                if (typeof newData === 'function') {
                    const currentCache = queryClient.getQueryData(['infinite', firstPageKey]);
                    const currentFlatData = currentCache?.pages?.flatMap(x => x.data);
                    resolved = newData(currentFlatData);
                }
                else {
                    resolved = await newData;
                }
                if (resolved !== undefined) {
                    queryClient.setQueryData(['infinite', firstPageKey], (old) => {
                        if (!old)
                            return old;
                        return {
                            ...old,
                            pages: [{ data: resolved, meta: old.pages[0]?.meta || { code: 200, status_code: 200, message: 'Success' }, pagination: old.pages[old.pages.length - 1]?.pagination }],
                            pageParams: [0],
                        };
                    });
                }
                if (shouldRevalidate !== false) {
                    await refetch();
                }
                return resolved;
            }
            // Truncate to first page before refetching to avoid re-fetching all loaded pages
            queryClient.setQueryData(['infinite', firstPageKey], (old) => {
                if (!old) {
                    return old;
                }
                return {
                    ...old,
                    pages: old.pages.slice(0, 1),
                    pageParams: old.pageParams.slice(0, 1),
                };
            });
            const result = await refetch();
            return result.data?.pages?.flatMap(x => x.data);
        }, [firstPageKey, queryClient, refetch]);
        const revalidate = useCallback(async () => {
            const queryKey = ['infinite', firstPageKey];
            const url = getKeyFnRef.current(0, undefined);
            if (!url) {
                const currentData = queryClient.getQueryData(queryKey);
                return currentData?.pages?.flatMap(x => x.data);
            }
            const firstPage = await fetchFn(url);
            queryClient.setQueryData(queryKey, (old) => {
                if (!old) {
                    return {
                        pages: [firstPage],
                        pageParams: [0],
                    };
                }
                return {
                    ...old,
                    pages: [firstPage, ...old.pages.slice(1)],
                };
            });
            const updated = queryClient.getQueryData(queryKey);
            return updated?.pages?.flatMap(x => x.data);
        }, [firstPageKey, queryClient, fetchFn]);
        return {
            data: flattenedData,
            mutate,
            revalidate,
            pagination,
            size,
            setSize,
            isLoadingMore,
            isValidating: isFetching,
            isLoading,
            error: error ?? undefined,
        };
    }
    return {
        useApi,
        useApiPagination,
        post,
        put,
        patch,
        get,
        del
    };
}
;
